import { isDefined } from './isDefined'
import type { Result, SuccessResult } from './result'
import { result as R } from './result'


type NullableError = 'NullableError'

interface Pipe<A, E = never> {
  map: <R>(fn: (value: A) => R) => Pipe<R, E>
  get: () => A
  getOrElse: <R>(fallback: (e: E) => R) => A | R
  throwNullable: () => Pipe<NonNullable<A>, NullableError>
  throwWhen: <Err>(predicate: (value: A) => boolean, mapError: (value: A) => Err) => Pipe<A, E | Err>
  flatMap: <R>(fn: (value: A) => Pipe<R, E>) => Pipe<R, E>
  mapNullable: <R, Fallback>(fn: (value: A) => R, fallbackFn: () => Fallback) => Pipe<R | Fallback, E>
  mapWhen: <R>(predicate: (value: A) => boolean, fn: (value: A) => R) => Pipe<R | A, E>
  mapError: <RE>(fn: (error: E) => RE) => Pipe<A, RE>
  tap: (fn: (value: A) => unknown) => Pipe<A, E>
  tapWhen: (predicate: (value: A) => boolean, fn: (value: A) => unknown) => Pipe<A, E>
  tapError: (fn: (error: E) => unknown) => Pipe<A, E>
  trySafe: <R, Fail>(fn: (value: A) => R, onError: (e: unknown) => Fail) => Pipe<R | Fail, E>
  try: <R, Fail>(fn: (value: A) => R, onError?: (e: unknown) => Fail) => Pipe<R, E | Fail>
}

const resolveSuccessValue = <A, E>(result: Result<A, E>) => {
  if (R.isSuccess(result)) {
    return result.data
  }

  throw new Error(result.error as unknown as string)
}

export const pipe = <A, E = never>(initFn: () => A): Pipe<A, E> => {
  return pipeInternal(() => R.success(initFn()))
}

export const pipeInternal = <A, E>(initFn: () => Result<A, E>): Pipe<A, E> => {
  const processSuccessValue = <R>(fn: (value: A) => R): Pipe<R, E> => {
    return pipeInternal(() => {
      const result = initFn()
      if (R.isSuccess(result)) {
        return R.success(fn(result.data))
      }

      return result
    })
  }

  const processErrorValue = <R>(fn: (error: E) => R): Pipe<A, R> => {
    return pipeInternal(() => {
      const result = initFn()
      if (R.isError(result)) {
        return R.error(fn(result.error))
      }

      return result
    })
  }

  return {
    map: <R>(fn: (value: A) => R): Pipe<R, E> => {
      return processSuccessValue(fn)
    },
    throwNullable: () => {
      return pipeInternal(() => {
        const result = initFn()
        if (R.isSuccess(result) && isDefined(result.data)) {
          return result as SuccessResult<NonNullable<A>>
        }

        return R.error('NullableError')
      })
    },
    throwWhen: <Err>(predicate: (value: A) => boolean, mapError: (value: A) => Err) => {
      return pipeInternal<A, E | Err>(() => {
        const result = initFn()
        if (R.isSuccess(result)) {
          return !predicate(result.data) ? result : R.error(mapError(result.data))
        }

        return result
      })
    },
    get: () => resolveSuccessValue(initFn()),
    getOrElse: <R>(fallback: (error: E) => R) => {
      const result = initFn()
      if (R.isSuccess(result)) {
        return result.data
      }

      return fallback(result.error)
    },
    flatMap: <R>(fn: (value: A) => Pipe<R, E>): Pipe<R, E> => {
      return processSuccessValue((value) => fn(value).get())
    },
    mapNullable: <R, Fallback>(fn: (value: A) => R, fallbackFn: () => Fallback) => {
      return processSuccessValue((value) => (isDefined(value) ? fn(value) : fallbackFn()))
    },
    mapWhen: <R>(predicate: (value: A) => boolean, fn: (value: A) => R) => {
      return processSuccessValue((value) => (predicate(value) ? fn(value) : value))
    },
    mapError: <R>(fn: (error: E) => R) => {
      return processErrorValue(fn)
    },
    tap: (fn: (value: A) => unknown) => {
      return processSuccessValue((value) => {
        fn(value)
        return value
      })
    },
    tapError: (fn: (value: E) => unknown) => {
      return processErrorValue((value) => {
        fn(value)
        return value
      })
    },
    tapWhen: (predicate: (value: A) => boolean, fn: (value: A) => unknown) => {
      return processSuccessValue((value) => {
        if (predicate(value)) {
          fn(value)
        }
        return value
      })
    },
    trySafe: <R, Fail>(fn: (value: A) => R, onError: (e: unknown) => Fail) => {
      return processSuccessValue((value) => {
        try {
          return fn(value)
        } catch (e) {
          return onError(e)
        }
      })
    },
    try: <R, Fail>(fn: (value: A) => R, onError: (e: unknown) => Fail = (e) => e as Fail): Pipe<R, E | Fail> => {
      return pipeInternal<R, E | Fail>(() => {
        const value = initFn()
        if (R.isSuccess(value)) {
          try {
            return R.success(fn(value.data))
          } catch (e) {
            return R.error(onError(e))
          }
        }
        return value
      })
    }
  }
}
